// Chakra imports
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Image,
  Portal,
  Progress,
  SimpleGrid,
  Spacer,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  Badge,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";

import { NavLink } from "react-router-dom";

// assets
import peopleImage from "assets/img/people-image.png";
import logoChakra from "assets/svg/logo-white.svg";
// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import BarChart from "components/Charts/BarChart";
import LineChart from "components/Charts/LineChart";
import IconBox from "components/Icons/IconBox";

import axios from "axios";
import { API_SERVER } from "config/constant";
// Custom icons
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
  RocketIcon,
  StatsIcon,
  WalletIcon,
} from "components/Icons/Icons.js";
import DashboardTableRow from "components/Tables/DashboardTableRow";
import TimelineRow from "components/Tables/TimelineRow";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
// react icons
import { BsArrowRight } from "react-icons/bs";
import { IoCheckmarkDoneCircleSharp } from "react-icons/io5";
import { dashboardTableData, timelineData } from "variables/general";

export default function Dashboard() {
  const value = "$100.000";

  // Chakra Color Mode
  const { colorMode, toggleColorMode } = useColorMode();
  const iconTeal = useColorModeValue("teal.300", "teal.300");
  const iconBoxInside = useColorModeValue("white", "white");
  const textColor = useColorModeValue("gray.700", "white");
  const bgStatus = useColorModeValue("gray.400", "#1a202c");
  const colorStatus = useColorModeValue("white", "gray.400");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    inProcess: 0,
    activeCarriers: 0,
    dispatchedProducts: 0,
  });

  const [series, setSeries] = useState([
    {
      type: "area",
      name: "Mobile apps",
      data: [190, 220, 205, 350, 370, 450, 400, 360, 210, 250, 292, 150],
    },
    {
      type: "area",
      name: "Websites",
      data: [400, 291, 121, 117, 25, 133, 121, 211, 147, 25, 201, 203],
    },
  ]);
  const overlayRef = React.useRef();

  useEffect(() => {
    getOrders();
  }, []);

  const getOrders = async () => {
    await axios
      .get(`${API_SERVER}orders/get_list?page=` + 1)
      .then(({ data }) => {
        setOrders(data.data);
        // Calculate stats
        const total = data.data.length;
        const inProcess = data.data.filter(
          (order) => order.status !== "Completado"
        ).length; // Assume status text
        setStats({
          totalOrders: total,
          inProcess: inProcess,
          activeCarriers: 320, // Keep hardcoded for now
          dispatchedProducts: 25000,
        });
      });
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SimpleGrid
          columns={{ base: 1, sm: 1, md: 2, lg: 3, xl: 4 }}
          spacing={{ base: "16px", md: "20px", xl: "24px" }}
        >
          <Card
            minH="83px"
            bgGradient="linear(to-r, teal.300, blue.500)"
            shadow="lg"
            borderRadius="lg"
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
          >
            <CardBody>
              <Flex
                flexDirection="row"
                align="center"
                justify="center"
                w="100%"
              >
                <Stat me="auto">
                  <StatLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    color="white"
                    fontWeight="bold"
                    pb=".1rem"
                    textShadow="0 1px 2px rgba(0,0,0,0.3)"
                  >
                    Pedidos
                  </StatLabel>
                  <Flex>
                    <StatNumber
                      fontSize={{ base: "md", md: "lg", xl: "xl" }}
                      color="white"
                      fontWeight="bold"
                    >
                      {stats.totalOrders}
                    </StatNumber>
                  </Flex>
                </Stat>
                <IconBox
                  as="box"
                  h={{ base: "35px", md: "45px" }}
                  w={{ base: "35px", md: "45px" }}
                  bg={iconTeal}
                >
                  <WalletIcon
                    h={{ base: "18px", md: "24px" }}
                    w={{ base: "18px", md: "24px" }}
                    color={iconBoxInside}
                  />
                </IconBox>
              </Flex>
            </CardBody>
          </Card>
          <Card
            minH="83px"
            bgGradient="linear(to-r, green.300, teal.500)"
            shadow="lg"
            borderRadius="lg"
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
          >
            <CardBody>
              <Flex
                flexDirection="row"
                align="center"
                justify="center"
                w="100%"
              >
                <Stat me="auto">
                  <StatLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    color="white"
                    fontWeight="bold"
                    pb=".1rem"
                    textShadow="0 1px 2px rgba(0,0,0,0.3)"
                  >
                    En proceso
                  </StatLabel>
                  <Flex>
                    <StatNumber
                      fontSize={{ base: "md", md: "lg", xl: "xl" }}
                      color="white"
                      fontWeight="bold"
                    >
                      {stats.inProcess}
                    </StatNumber>
                  </Flex>
                </Stat>
                <IconBox
                  as="box"
                  h={{ base: "35px", md: "45px" }}
                  w={{ base: "35px", md: "45px" }}
                  bg={iconTeal}
                >
                  <GlobeIcon
                    h={{ base: "18px", md: "24px" }}
                    w={{ base: "18px", md: "24px" }}
                    color={iconBoxInside}
                  />
                </IconBox>
              </Flex>
            </CardBody>
          </Card>
          <Card
            minH="83px"
            bgGradient="linear(to-r, purple.300, pink.500)"
            shadow="lg"
            borderRadius="lg"
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
          >
            <CardBody>
              <Flex
                flexDirection="row"
                align="center"
                justify="center"
                w="100%"
              >
                <Stat>
                  <StatLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    color="white"
                    fontWeight="bold"
                    pb=".1rem"
                    textShadow="0 1px 2px rgba(0,0,0,0.3)"
                  >
                    Transportistas activos
                  </StatLabel>
                  <Flex>
                    <StatNumber
                      fontSize={{ base: "md", md: "lg", xl: "xl" }}
                      color="white"
                      fontWeight="bold"
                    >
                      {stats.activeCarriers}
                    </StatNumber>
                  </Flex>
                </Stat>
                <Spacer />
                <IconBox
                  as="box"
                  h={{ base: "35px", md: "45px" }}
                  w={{ base: "35px", md: "45px" }}
                  bg={iconTeal}
                >
                  <DocumentIcon
                    h={{ base: "18px", md: "24px" }}
                    w={{ base: "18px", md: "24px" }}
                    color={iconBoxInside}
                  />
                </IconBox>
              </Flex>
            </CardBody>
          </Card>
          <Card
            minH="83px"
            bgGradient="linear(to-r, orange.300, red.500)"
            shadow="lg"
            borderRadius="lg"
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
          >
            <CardBody>
              <Flex
                flexDirection="row"
                align="center"
                justify="center"
                w="100%"
              >
                <Stat me="auto">
                  <StatLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    color="white"
                    fontWeight="bold"
                    pb=".1rem"
                    textShadow="0 1px 2px rgba(0,0,0,0.3)"
                  >
                    Productos despachados
                  </StatLabel>
                  <Flex>
                    <StatNumber fontSize="lg" color="white" fontWeight="bold">
                      {stats.dispatchedProducts}
                    </StatNumber>
                  </Flex>
                </Stat>
                <IconBox
                  as="box"
                  h={{ base: "35px", md: "45px" }}
                  w={{ base: "35px", md: "45px" }}
                  bg={iconTeal}
                >
                  <CartIcon
                    h={{ base: "18px", md: "24px" }}
                    w={{ base: "18px", md: "24px" }}
                    color={iconBoxInside}
                  />
                </IconBox>
              </Flex>
            </CardBody>
          </Card>
        </SimpleGrid>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card
          p="16px"
          mt="16px"
          overflowX={{ sm: "scroll", xl: "hidden" }}
          shadow="lg"
          borderRadius="lg"
          transition="all 0.3s ease"
          _hover={{ shadow: "xl" }}
        >
          <CardHeader p="12px 0px 28px 0px">
            <Flex direction="column">
              <Text
                fontSize={{ base: "md", md: "lg", xl: "xl" }}
                color={textColor}
                fontWeight="bold"
                pb=".5rem"
              >
                Órdenes a lo largo del tiempo
              </Text>
            </Flex>
          </CardHeader>
          <CardBody>
            <LineChart series={series} />
          </CardBody>
        </Card>
      </motion.div>

      <Spacer></Spacer>

      <Card
        p="16px"
        mt="16px"
        overflowX={{ sm: "scroll", xl: "hidden" }}
        shadow="lg"
        borderRadius="lg"
        transition="all 0.3s ease"
        _hover={{ shadow: "xl" }}
      >
        <CardHeader p="12px 0px 28px 0px">
          <Flex direction="column">
            <Text fontSize="lg" color={textColor} fontWeight="bold" pb=".5rem">
              Pedidos recientes
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Table
            variant="simple"
            color={textColor}
            size={{ base: "sm", md: "md" }}
          >
            <Thead>
              <Tr my=".8rem" pl="0px" color="gray.400">
                <Th pl="0px" color="gray.400">
                  Código
                </Th>
                <Th color="gray.400">Total</Th>
                <Th color="gray.400">Estado</Th>
                <Th color="gray.400">Fecha</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orders.slice(0, 5).map((row, key) => (
                <Tr key={key}>
                  <Td>
                    <Flex direction="column">
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.code}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex direction="column">
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.total}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Badge
                      bg={
                        row.status === "Completado" ? "green.400" : "orange.400"
                      }
                      color="white"
                      fontSize="14px"
                      p="3px 8px"
                      borderRadius="8px"
                    >
                      {row.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex direction="column">
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {row.date_add}
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
      {/* <Card maxH="100%">
          <CardHeader p="22px 0px 35px 14px">
            <Flex direction="column">
              <Text
                fontSize={{ base: "md", md: "lg", xl: "xl" }}
                color={textColor}
                fontWeight="bold"
                pb=".5rem"
              >
                Orders overview
              </Text>
              <Text fontSize="sm" color="gray.400" fontWeight="normal">
                <Text fontWeight="bold" as="span" color="teal.300">
                  +30%
                </Text>{" "}
                this month.
              </Text>
            </Flex>
          </CardHeader>
          <CardBody ps="20px" pe="0px" mb="31px" position="relative">
            <Flex direction="column">
              {timelineData.map((row, index, arr) => {
                return (
                  <TimelineRow
                    logo={row.logo}
                    title={row.title}
                    date={row.date}
                    color={row.color}
                    index={index}
                    arrLength={arr.length}
                  />
                );
              })}
            </Flex>
          </CardBody>
        </Card> */}
    </Flex>
  );
}
